import React, { FC, ReactText, useCallback, useMemo, useRef } from 'react';
import { Dropdown, MenuProps } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { PaginationConfig } from 'antd/lib/pagination';
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import moment from 'moment';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { exportReportToExcel, exportReportToPdf } from '@core/utils/methods';
import {
  OptionProps,
  OverdueTaskRow,
  OverdueTasksReportResponse,
  ReportsBucket,
  ReportsFilters,
  ReportsNamedCount,
} from '@models/interfaces';
import { CustomTable } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import type { ReportsSummaryMetric } from '../../models';
import {
  AGENT_CHART_COLORS,
  FULL_FILTER_CAPTION,
  getAgentChartData,
  getOverdueReportRowClassName,
  getOverdueReportRowKey,
  getOverdueTaskColumns,
  OVERDUE_BUCKET_COLORS,
} from '../../models';
import { ReportChartCard } from '../report-chart-card';
import { ReportsSummary } from '../reports-summary';

import './styles.scss';

interface OverdueTasksReportProps {
  data?: OverdueTasksReportResponse;
  filters: ReportsFilters;
  onRowClick: (record: OverdueTaskRow, event: any) => void;
  onTableChange: (
    pagination: PaginationConfig,
    tableFilters: Record<string, ReactText[] | null>,
    sorter: SorterResult<Store> | SorterResult<Store>[],
    extra: TableCurrentDataSource<Store>,
  ) => void;
  sortByOptions: OptionProps[];
}

export const OverdueTasksReport: FC<OverdueTasksReportProps> = ({
  data,
  filters,
  onRowClick,
  onTableChange,
  sortByOptions,
}: OverdueTasksReportProps) => {
  const chartsRef = useRef<HTMLDivElement>(null);
  const rows = data?.table.items ?? [];
  const total = data?.table.total ?? 0;
  const overdueDaysBuckets = data?.charts.overdueDaysBuckets ?? [];
  const overdueByAgent = getAgentChartData(data?.charts.overdueByAgent);
  const columns = useMemo(
    () => getOverdueTaskColumns(filters, sortByOptions),
    [filters, sortByOptions],
  );
  const metrics: ReportsSummaryMetric[] = [
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Total Overdue',
      value: data?.summary.totalOverdue ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Average Days Overdue',
      value: data?.summary.averageDaysOverdue ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Max Days Overdue',
      value: data?.summary.maxDaysOverdue ?? 0,
    },
  ];

  const onExportExcel = useCallback((): void => {
    exportReportToExcel({
      columns,
      fileName: `Overdue-Tasks-${moment().format('YYYY-MM-DD-HHmm')}.xlsx`,
      metrics,
      reportTitle: 'Overdue Tasks',
      rows,
    });
  }, [columns, metrics, rows]);

  const onExportPdf = useCallback((): void => {
    exportReportToPdf({
      chartsElement: chartsRef.current,
      columns,
      fileName: `Overdue-Tasks-${moment().format('YYYY-MM-DD-HHmm')}.pdf`,
      metrics,
      reportTitle: 'Overdue Tasks',
      rows,
    });
  }, [columns, metrics, rows]);

  const exportMenu: MenuProps = {
    items: [
      { key: 'excel', label: 'Export as Excel', onClick: onExportExcel },
      { key: 'pdf', label: 'Export as PDF', onClick: onExportPdf },
    ],
  };

  return (
    <div className="prov-overdue-tasks-report">
      <div className="prov-overdue-tasks-report__toolbar">
        <p className="prov-overdue-tasks-report__description">
          Tasks whose forecasted date has already passed with no actual
          completion date recorded. Only shows work that is late relative to its
          forecast.
        </p>
        <Dropdown disabled={!rows.length} menu={exportMenu}>
          <PrimaryButton title="Export ▾" />
        </Dropdown>
      </div>

      <ReportsSummary metrics={metrics} />

      <div className="prov-overdue-tasks-report__charts" ref={chartsRef}>
        <ReportChartCard
          hasData={overdueDaysBuckets.some(
            ({ count }: ReportsBucket) => count > 0,
          )}
          title="Overdue Days Buckets"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={overdueDaysBuckets}
              layout="vertical"
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke="rgba(255,255,255,0.12)" />
              <XAxis
                allowDecimals={false}
                stroke="rgba(255,255,255,0.65)"
                type="number"
              />
              <YAxis
                dataKey="label"
                stroke="rgba(255,255,255,0.65)"
                type="category"
                width={72}
              />
              <Tooltip />
              <Bar dataKey="count" name="Tasks">
                {overdueDaysBuckets.map(
                  ({ label }: ReportsBucket, index: number) => (
                    <Cell
                      fill={
                        OVERDUE_BUCKET_COLORS[
                          index % OVERDUE_BUCKET_COLORS.length
                        ]
                      }
                      key={label}
                    />
                  ),
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ReportChartCard>

        <ReportChartCard
          hasData={overdueByAgent.some(
            ({ count }: ReportsNamedCount) => count > 0,
          )}
          title="Overdue By Assigned Agent"
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Tooltip />
              <Legend />
              <Pie
                data={overdueByAgent}
                dataKey="count"
                innerRadius={58}
                nameKey="name"
                outerRadius={86}
                paddingAngle={2}
              >
                {overdueByAgent.map(
                  ({ id, name }: ReportsNamedCount, index: number) => (
                    <Cell
                      fill={
                        AGENT_CHART_COLORS[index % AGENT_CHART_COLORS.length]
                      }
                      key={id || name || index}
                    />
                  ),
                )}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </ReportChartCard>
      </div>

      <CustomTable
        className="prov-overdue-tasks-report__table"
        columns={columns}
        dataSource={rows}
        onChange={onTableChange}
        onRowClick={onRowClick}
        pagination={{
          current: filters.page,
          pageSize: filters.size,
          total,
        }}
        rowClassName={getOverdueReportRowClassName}
        rowKey={getOverdueReportRowKey}
        scroll={{ x: 2400, y: 'auto' }}
      />
    </div>
  );
};
